import React, { useState, useEffect } from "react";
import { useAuthStore } from "../../../store/authStore";
import { useTicketStore } from "../../../store/ticketStore";
import { format } from "date-fns";
import DailyDetailModal from "./DailyDetailModal";

export default function TicketDaily() {
	const { user } = useAuthStore();
	const {
		leftTickets,
		rightTickets,
		isLoading,
		error,
		getSeatReservationStatus,
		getSeatReservationStatusBoth,
	} = useTicketStore();

	const [leftDate, setLeftDate] = useState(format(new Date(), "yyyy-MM-dd"));
	const [rightDate, setRightDate] = useState(format(new Date(), "yyyy-MM-dd"));
	const [leftSearchQuery, setLeftSearchQuery] = useState("");
	const [rightSearchQuery, setRightSearchQuery] = useState("");

	// 모달 상태 관리
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedTicketId, setSelectedTicketId] = useState(null);
	const [trainNumber, setTrainNumber] = useState(null);
	const [departureDate, setDepartureDate] = useState(null);
	const [departureStation, setDepartureStation] = useState(null);
	const [arrivalStation, setArrivalStation] = useState(null);

	// 날짜 변경 핸들러
	const handleLeftDateChange = (e) => {
		setLeftDate(e.target.value);
	};

	const handleRightDateChange = (e) => {
		setRightDate(e.target.value);
	};

	// 검색어 변경 핸들러
	const handleLeftSearchChange = (e) => {
		setLeftSearchQuery(e.target.value);
	};

	const handleRightSearchChange = (e) => {
		setRightSearchQuery(e.target.value);
	};

	// 검색 버튼 핸들러
	const handleSearch = (type) => {
		if (type === 'left') {
			getSeatReservationStatus(leftDate, type, leftSearchQuery);
		} else {
			getSeatReservationStatus(rightDate, type, rightSearchQuery);
		}
	};

	// 양쪽 모두 검색
	const handleSearchBoth = () => {
		getSeatReservationStatus(leftDate, 'left', leftSearchQuery);
		getSeatReservationStatus(rightDate, 'right', rightSearchQuery);
	};

	// 배치보기 버튼 클릭 핸들러
	const handleViewDetails = (ticketIds, trainNumber, departureDate, departureStation, arrivalStation) => {
		setSelectedTicketId(ticketIds.join(","));
    setTrainNumber(trainNumber);
    setDepartureDate(departureDate);
    setDepartureStation(departureStation);
    setArrivalStation(arrivalStation);
		setIsModalOpen(true);
	};

	// 모달 닫기 핸들러
	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedTicketId(null);
	};

	// 티켓 테이블 렌더링 함수
	const renderTicketTable = (tickets, title, subTitle) => (
		<div className="bg-white shadow rounded-lg overflow-hidden">
			<div className="bg-blue-600 text-white p-3 flex items-center">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					className="h-5 w-5 mr-2"
					viewBox="0 0 20 20"
					fill="currentColor"
				>
					<path
						fillRule="evenodd"
						d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
						clipRule="evenodd"
					/>
				</svg>
				<span className="font-bold">
					{title} ({subTitle})
				</span>
			</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th
								scope="col"
								className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								번호
							</th>
							<th
								scope="col"
								className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								출발일자
							</th>
							<th
								scope="col"
								className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								열차번호
							</th>
							<th
								scope="col"
								className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								출/도착역
							</th>
							<th
								scope="col"
								className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								배정현황
							</th>
							<th
								scope="col"
								className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
							>
								배치보기
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{tickets.length > 0 ? (
							tickets.map((ticket, index) => (
								<tr key={ticket.ticket_item_ids.toString()}>
									<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
										{index + 1}
									</td>
									<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
										{ticket.departure_time.split(" ")[0]}
									</td>
									<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
										{ticket.train_code}
									</td>
									<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
										{ticket.departure_arrival_station}
									</td>
									<td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
										{ticket.assigned_seat_cnt} /{" "}
										{ticket.seat_cnt}
									</td>
									<td className="px-4 py-2 whitespace-nowrap text-sm">
										<button
											onClick={() =>
												handleViewDetails(
													ticket.ticket_item_ids,
													ticket.train_code,
													ticket.departure_time,
													ticket.departure_arrival_station.split(" → ")[0].replace(/역$/, ""),
													ticket.departure_arrival_station.split(" → ")[1].replace(/역$/, ""),
												)
											}
											className="bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded text-xs"
										>
											배치보기
										</button>
									</td>
								</tr>
							))
						) : (
							<tr key="no-data">
								<td
									colSpan="6"
									className="px-4 py-4 text-center text-sm text-gray-500"
								>
									{isLoading
										? "데이터를 불러오는 중..."
										: "표시할 데이터가 없습니다."}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);

	// 컴포넌트 마운트 시 초기 데이터 로드
	useEffect(() => {
		handleSearchBoth();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">
				일자별 승차권 보유 현황
			</h1>
			<p className="text-gray-500 mb-6">
				선택한 일자의 승차권 현황을 조회하는 화면입니다.
			</p>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
				{/* 왼쪽 검색 영역 */}
				<div className="flex items-center space-x-2 justify-end">
					<div className="flex items-center">
						<label
							htmlFor="leftDate"
							className="mr-2 text-sm font-medium text-gray-700"
						>
							출발일자
						</label>
						<input
							type="date"
							id="leftDate"
							value={leftDate}
							onChange={(e) => {
								setLeftDate(e.target.value);
								setRightDate(e.target.value);
							}}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
							style={{ colorScheme: "light" }}
						/>
					</div>
					<div className="flex items-center">
						<input
							type="text"
							placeholder="열차번호 또는 출/도착역"
							value={leftSearchQuery}
							onChange={handleLeftSearchChange}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm flex-1"
						/>
					</div>
					<button
						onClick={() => handleSearch("left")}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
					>
						검색하기
					</button>
				</div>

				{/* 오른쪽 검색 영역 */}
				<div className="flex items-center space-x-2 justify-end">
					<div className="flex items-center">
						<label
							htmlFor="rightDate"
							className="mr-2 text-sm font-medium text-gray-700"
						>
							출발일자
						</label>
						<input
							type="date"
							id="rightDate"
							value={rightDate}
							onChange={handleRightDateChange}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
							style={{ colorScheme: "light" }}
						/>
					</div>
					<div className="flex items-center">
						<input
							type="text"
							placeholder="열차번호 또는 출/도착역"
							value={rightSearchQuery}
							onChange={handleRightSearchChange}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm flex-1"
						/>
					</div>
					<button
						onClick={() => handleSearch("right")}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm"
					>
						검색하기
					</button>
				</div>
			</div>

			{/* 에러 메시지 표시 */}
			{error && (
				<div
					className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6"
					role="alert"
				>
					<span className="block sm:inline">{error}</span>
				</div>
			)}

			{/* 티켓 테이블 영역 */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
				{/* 왕편 티켓 */}
				<div>{renderTicketTable(leftTickets, "왕편", "가는 열차")}</div>

				{/* 복편 티켓 */}
				<div>
					{renderTicketTable(rightTickets, "복편", "오는 열차")}
				</div>
			</div>

			{/* 티켓 상세 모달 */}
			<DailyDetailModal
				ticketItemIdsStr={selectedTicketId}
				trainNumber={trainNumber}
				departureDate={departureDate}
				departureStation={departureStation}
				arrivalStation={arrivalStation}
				isOpen={isModalOpen}
				onClose={handleCloseModal}
			/>
		</div>
	);
}
